// hooks
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useRef,
  useState,
} from 'react'
import { Client, MountpointMap, useClientsMap, useMountpointsMap } from 'api'
//
import L from 'leaflet'

export type MountpointFilters =
  | 'connected'
  | 'unstable'
  | 'disconnected'
  | 'inactive'
  | 'coordAlert'
  | 'auto'

export type ClientFilters = 'rtkFixed' | 'rtkFloat' | 'dgnss' | 'notFixed'

interface MapContextType {
  mapRef: React.RefObject<L.Map>
  mountpointsClusterRef: React.RefObject<L.MarkerClusterGroup>
  // line distance
  positions: L.LatLng[]
  setLinePositions: (latLngs: L.LatLng[]) => void
  // mountpoints
  mountpointFilters: { [key in MountpointFilters]: boolean }
  setMountpointFilters: React.Dispatch<
    React.SetStateAction<{
      [key in MountpointFilters]: boolean
    }>
  >
  mountpoints: MountpointMap[]
  flyToMountpointPosition: (mountpointId: number) => void
  // clients
  clientFilters: { [key in ClientFilters]: boolean }
  setClientFilters: React.Dispatch<
    React.SetStateAction<{
      [key in ClientFilters]: boolean
    }>
  >
  clients: Client[]
  flyToClientPosition: (clientId: number) => void
}

const MapContext = createContext<MapContextType>({} as MapContextType)

const MapProvider = ({ children }: PropsWithChildren) => {
  const mapRef = useRef<L.Map>(null)
  const mountpointsClusterRef = useRef<L.MarkerClusterGroup | null>(null)

  // line distance
  const [positions, setPositions] = useState<L.LatLng[]>([])

  const setLinePositions = (latLngs: L.LatLng[]) => {
    setPositions(latLngs)
  }

  // mountpoints
  const {
    connectedMountpoints,
    unstableMountpoints,
    disconnectedMountpoints,
    inactiveMountpoints,
    coordAlertMountpoints,
    autoMountpoints,
  } = useMountpointsMap()

  const [mountpointFilters, setMountpointFilters] = useState({
    connected: true,
    unstable: true,
    disconnected: false,
    inactive: true,
    coordAlert: true,
    auto: true,
  })

  const getMountpoints = () => {
    let temp: MountpointMap[] = []

    if (mountpointFilters.connected) temp = temp.concat(connectedMountpoints)
    if (mountpointFilters.unstable) temp = temp.concat(unstableMountpoints)
    if (mountpointFilters.disconnected)
      temp = temp.concat(disconnectedMountpoints)
    if (mountpointFilters.inactive) temp = temp.concat(inactiveMountpoints)
    if (mountpointFilters.coordAlert) temp = temp.concat(coordAlertMountpoints)
    if (mountpointFilters.auto) temp = temp.concat(autoMountpoints)

    return temp
  }

  const flyToMountpointPosition = (mountpointId: number) => {
    const allMountpoints = [
      ...connectedMountpoints,
      ...unstableMountpoints,
      ...disconnectedMountpoints,
      ...inactiveMountpoints,
      ...coordAlertMountpoints,
      ...autoMountpoints,
    ]

    const clusterGroup = mountpointsClusterRef.current

    for (let mountpoint of allMountpoints) {
      if (mountpoint.id === mountpointId) {
        const lat = mountpoint.latitude
        const lng = mountpoint.longitude

        // Find the marker layer in the cluster group
        const layer = clusterGroup?.getLayers().find((layer) => {
          if (layer instanceof L.Marker) {
            const layerLatLng = layer.getLatLng()
            return layerLatLng.equals([lat, lng])
          }
          return false
        })

        if (layer && layer instanceof L.Marker) {
          // Check if the marker is currently in a cluster
          const visibleParent = clusterGroup?.getVisibleParent(layer)

          if (visibleParent !== layer) {
            // Marker is clustered, zoom to show it
            clusterGroup?.zoomToShowLayer(layer, () => {
              layer.openPopup()
            })
          } else {
            // Marker is not clustered, just fly to it and open popup
            mapRef.current?.flyTo([lat, lng], mapRef.current.getMaxZoom(), {
              animate: true,
            })
            // // Open popup after a short delay to ensure map has moved
            // setTimeout(() => {
            //   layer.openPopup()
            // }, 100)
          }
        } else {
          // Fallback: just fly to the coordinates
          mapRef.current?.flyTo([lat, lng], mapRef.current.getMaxZoom(), {
            animate: true,
          })
        }

        break
      }
    }
  }

  // clients
  const { notFixedClients, dgnssClients, rtkFixedClients, rtkFloatClients } =
    useClientsMap()

  const [clientFilters, setClientFilters] = useState({
    rtkFixed: true,
    rtkFloat: true,
    dgnss: true,
    notFixed: true,
  })

  const getClients = () => {
    let temp: Client[] = []
    if (clientFilters.notFixed) temp = temp.concat(notFixedClients)
    if (clientFilters.dgnss) temp = temp.concat(dgnssClients)
    if (clientFilters.rtkFixed) temp = temp.concat(rtkFixedClients)
    if (clientFilters.rtkFloat) temp = temp.concat(rtkFloatClients)

    return temp
  }

  const flyToClientPosition = (clientId: number) => {
    const allClients = [
      ...notFixedClients,
      ...dgnssClients,
      ...rtkFixedClients,
      ...rtkFloatClients,
    ]

    for (let client of allClients) {
      if (clientId === client.userId) {
        mapRef.current?.flyTo([client.latitude, client.longitude], 15, {
          animate: false,
        })
        break
      }
    }
  }

  return (
    <MapContext.Provider
      value={{
        mapRef,
        mountpointsClusterRef,
        positions,
        setLinePositions,
        //
        mountpointFilters,
        setMountpointFilters,
        mountpoints: getMountpoints(),
        flyToMountpointPosition,
        //
        clientFilters,
        setClientFilters,
        clients: getClients(),
        flyToClientPosition,
      }}
    >
      {children}
    </MapContext.Provider>
  )
}

export const useMapContext = () => useContext(MapContext)

export default MapProvider
